import _ from 'lodash';
import { Observable, of, fromEvent } from 'rxjs';
import { catchError, first, map, switchMap, takeWhile } from 'rxjs/operators';

import { MediaType, Media, AssociatedMedias } from '@skinsoft/types';
import { IndexedDBCache } from '@skinsoft/offline';
import { fetch, DEFAULT_PROXY } from '@skinsoft/network';

export class MediaTransferHelper {

    public static getInstance() {
        if (!MediaTransferHelper.instance) {
            MediaTransferHelper.instance = new MediaTransferHelper();
        }

        return MediaTransferHelper.instance;
    }

    public static install(Vue) {
        Vue.prototype.$mediaTransferHelper = MediaTransferHelper.getInstance();
    }

    private static instance: MediaTransferHelper = null as any;

    private proxy = DEFAULT_PROXY;

    public convertFileToBase64(file: File): Observable<string> {
        const reader = new FileReader()
        const eventListener$ = fromEvent(reader, 'load').pipe(
            map(() => {
                let base64 = reader.result
                if(base64 == null) {
                    base64 = '';
                }else if(base64 instanceof ArrayBuffer){
                    base64 = base64.toString();
                }
                return base64
            })
        )
        reader.readAsDataURL(file)
        return eventListener$
    }

    public convertBase64ToFile(base64: string, fileType: string): Blob {
        const byteString = atob(base64.split(',')[1])
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const intArray = new Uint8Array(arrayBuffer)       
                                
        for (let i = 0; i < byteString.length; i++) {
            intArray[i] = byteString.charCodeAt(i)
        }
                                
        return new Blob([intArray], {type: fileType})
    }

    public downloadMediaOffline(mediaId: string, cache: IndexedDBCache<any>): Observable<any> {
        return cache.read(mediaId).pipe(
            switchMap(cacheValue => {
                if(cacheValue) {
                    if(cacheValue instanceof File) {
                        return this.convertFileToBase64(cacheValue).pipe(
                            map(base64 => { 
                                return {dataUrl: base64, source: "cache"}
                            })
                        )
                    } else {
                        let strCacheValue = cacheValue as unknown as string;
                        // If the cache value is a data url it is the media, else it is the new mediaId received from backend
                        if(strCacheValue.startsWith('data:')) {
                            return of({ dataUrl: strCacheValue, source: "cache" })
                        }
                    }
                }
                
                return of({})
            }),
            takeWhile((res: any) => !res.dataUrl, true),
        )
    }

    public downloadMedia(mediaId: string, fileType = "vignette250", offlineLoading = false, cache: IndexedDBCache<any>) {

        let media$: Observable<any>

        // If the mediaId is a localId, we look in cache
        if (mediaId.startsWith('local:')) {
            this.debugLog("Retrieving  " + mediaId + " from cache")

            media$ = cache.read(mediaId).pipe(
                switchMap(
                    (cacheValue) => {
                        if (cacheValue) {
                            let strCacheValue = cacheValue as unknown as (string | File)
                            if(strCacheValue instanceof File) {
                                return this.convertFileToBase64(strCacheValue).pipe(
                                    map(base64 => {
                                        return { dataUrl: base64, source: "cache" }
                                    })
                                )
                            } 
                            else {
                                const fileName = mediaId.substring(mediaId.indexOf(':') + 1);
                                // If the cache value is a data url, we return it, the mediaId is not ready yet
                                if (strCacheValue.startsWith("data:")) {
    
                                    if (!offlineLoading && window.navigator.onLine) {
                                        // Fetching the id of the media notice from the filename (TODO, handle doubles)
                                        this.proxy.fetch('/medias/fileName',
                                            undefined,
                                            'POST',
                                            { fileName },
                                            false,
                                            true
                                        ).subscribe(
                                            media => {
                                                const isTranscodingTerminated = _.get(media, 'associatedFileRecords.proxy')
                                                if (media.id && isTranscodingTerminated) {
                                                    this.updateCacheTempKey(mediaId, media.id, cache);
                                                }
                                            },
                                            () => this.repeatReplaceMediaCacheKeyWithId(fileName, cache)
                                        )
                                    }
                                    return of({ dataUrl: strCacheValue, source: "cache" })
                                }
                                // If the cache value is not a data url, it is the new mediaId received from backend 
                                else {
                                    // Fetching data from cache with the mediaId as key
                                    return cache.read(strCacheValue).pipe(
                                        map(
                                            (secondCacheValue) => {
                                                // Deleting temporary cache entry
                                                cache.remove(mediaId)
                                                const strSecondCacheValue = secondCacheValue as unknown as string;
                                                // returning the data url and 'newMediaId' which can be used to update media identifiers 
                                                // in condition-report data (as in annotations for example)
                                                return { dataUrl: strSecondCacheValue, newMediaId: strCacheValue, source: "cache" }
                                            }
                                        )
                                    )
                                }
                            }
                        } else {
                            // No data in cache
                            return of({});
                        }
                    }
                ),
            )
        } else {
            // If the mediaId is not local (in cache), retrieving media from network
            this.debugLog("Retrieving " + mediaId + " from network")
            media$ = this.proxy.fetch("/downloadMedias",
                {
                    "mediaId": mediaId,
                    // tslint:disable-next-line:object-literal-sort-keys
                    "fileType": fileType,
                },
                'GET', undefined, undefined, true)
                .pipe(
                    map(media => {
                        // If a media is retrieved from network, we delete the cache entry and return the dataUrl
                        if (media) {
                            cache.read(mediaId).pipe(first()).subscribe(cacheEntry => {
                                if (!!cacheEntry) {
                                    cache.remove(mediaId);
                                }
                            })
                            return { dataUrl: media, source: "network" }
                        }
                    }),
                    catchError(errors => {
                        // In case of error (typically thumbnail not ready yet), fallback on cached media if exists
                        this.debugLog("Media with id '" + mediaId + "' not available from network, falling back on cache")
                        return cache.read(mediaId).pipe(
                            first(),
                            map(entry => {
                                return { dataUrl: entry, source: "cache" }
                            })
                        )
                    },
                    ),
                );
        }
        return media$;
    }
    public repeatReplaceMediaCacheKeyWithId(fileName: string, cache: IndexedDBCache<any>): void {
        this.findMediaIdFromFileName(fileName)
        .subscribe(
                media => {
                    const isTranscodingTerminated = _.get(media, 'associatedFileRecords.proxy')
                    if (media.id) {
                        this.updateCacheTempKey('local:'+fileName, media.id, cache);
                    }
                },
                () => setTimeout(() => this.repeatReplaceMediaCacheKeyWithId(fileName, cache), 500)
            )
    }

    public replaceMediaCacheKeyWithIdFromFileName(fileName: string, cache: IndexedDBCache<any>) {
        this.findMediaIdFromFileName(fileName)
        .subscribe(
            media => {
                const isTranscodingTerminated = _.get(media, 'associatedFileRecords.proxy')
                if (media.id && isTranscodingTerminated) {
                    this.updateCacheTempKey('local:'+fileName, media.id, cache);
                }
            },
            () =>  this.debugLog("Media with name " + fileName + " not found on backend")
        )
    }

    public findMediaIdFromFileName(fileName: string): Observable<Media> {
        return this.proxy.fetch('/medias/fileName',
            undefined,
            'POST',
            { fileName },
            false,
            true
        )
    }

    public repeatFindingMediaIdFromFileName(fileName: string) {
        return new Promise<Media>((resolve, reject) => {

            const getMedia = () => {
                this.findMediaIdFromFileName(fileName)
                .subscribe(
                    media => {
                        if(media.id) {
                            resolve(media)
                        }
                    },
                    () => setTimeout(() => getMedia(), 500)
                )
            }

            getMedia()
        })
    }

    public uploadMedia(mimeType: string, file: File, localUrl: string, cache: IndexedDBCache<any>, isAssociatedDocument?, isOffline?: boolean): string {

        // Setting image file in request body
        const formData = new FormData();
        const fileName =  "REP-"+Date.now()+"."+ file.name.split('.').pop()
        formData.append("files", file, fileName)

        // We set immediately the image in cache with a generated temp id
        // const tempKey = `local:${this.generateImageHash()}`
        const tempKey = `local:${fileName}`
        this.setMediaInCache(tempKey, localUrl, cache)
        if (!isOffline) {
            // Creating new empty media notice
            fetch.api(`/uploadMedias/filesGroup?mimeTypes=${mimeType}`, { method: 'PUT', body: formData }).pipe(
                switchMap(
                    groupId => {
                        return fetch.api(`/uploadMedias/beginIngest?groupId=${groupId}&isPrincipal=true&isAssociatedDocument=${!!isAssociatedDocument}`,
                            {
                                method: 'PUT',
                            })
                    }
                )
            ).subscribe(
                (response: any) => {
                    // When media is ready replace temp cache key by media notice id
                    this.debugLog("Set image in cache with key : " + response.mediaId)
                })
        }
        return tempKey;
    }

    public uploadMediaInCache(file: File, localUrl: string, cache: IndexedDBCache<any>): string {

        // Setting image file in request body
        const formData = new FormData();
        formData.append("files", file)

        // We set immediately the image in cache with a generated temp id
        // const tempKey = `local:${this.generateImageHash()}`
        const tempKey = `local:${file.name}`
        this.setMediaInCache(tempKey, file, cache)

        return tempKey;
    }

    public getMediaType(cacheKey: string): MediaType {
        const pdfRegex = /.*\.pdf/
        if(pdfRegex.test(cacheKey)) {
            return MediaType.PDF
        }
        else {
            return MediaType.IMAGE
        }
    }

    private debugLog(message: any) {
        // tslint:disable-next-line:no-console
        console.debug("[Media-cache-management] " + JSON.stringify(message))
    }

    private setMediaInCache(mediaId, localUrl, cache: IndexedDBCache<any>) {
        cache.write(mediaId, localUrl)
    }

    /**
     * Changes the value of a key with another key,so that the next attempt
     * to access the first key data leads to use the new key
     * @param keyToReplace 
     * @param newKey 
     */
    private async updateCacheTempKey(keyToReplace, newKey, cache: IndexedDBCache<any>) {
        cache.read(keyToReplace).pipe(first()).subscribe(
            cacheEntry => {
                if (!!cacheEntry) {
                    cache.write(keyToReplace, newKey);
                    cache.write(newKey, cacheEntry);
                    this.debugLog("Cache entry '" + keyToReplace + "' has been replaced with '" + newKey + "'")
                }
            }
        );
    }

    /**
     * Generates a random id of fixed length 
     * @param value 
     */
    public static generateMediaHash() {
        return '_' + Math.random().toString(36).substr(2, 9) + '-' + Math.random().toString(36).substr(2, 9)
    }

    public static isLocalMedia(mediaId: string): boolean {
        return mediaId.startsWith('local')
    }

    public static permutateFirstMedia(newFirstMedia: string, associatedMedias: AssociatedMedias): AssociatedMedias {
        let result = {
            ...associatedMedias
        }
        
        result.first = newFirstMedia

        if(associatedMedias.first && associatedMedias.first !== newFirstMedia) {
            result.second = associatedMedias.first
            if(associatedMedias.second && associatedMedias.second !== newFirstMedia) { 
                result.third = associatedMedias.second
            }
        }

        if(associatedMedias.second && associatedMedias.second === newFirstMedia) {
            if(associatedMedias.third && associatedMedias.third !== newFirstMedia && result.second === associatedMedias.second) {
                result.second = associatedMedias.third
            }
        }

        if(associatedMedias.third === newFirstMedia && result.third === associatedMedias.third) {
            delete result.third
        }

        return result
    }
}